import React, { Component, MouseEvent } from "react";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { navigateWithInParent, replaceNavigate } from "@my-dld/utils/StoreUtils";
import PageTitle from "@my-dld/components/PageTitle";
import { DashboardBCItem, MyProfileBCItem } from "@my-dld/contexts/PageContext";
import PageContent from "@my-dld/components/PageContent";
import TextInputField from "@my-dld/components/Form/TextInputField";
import { LocaleUtil, StringUtil } from "@eres/core";
import { SSOApi } from "@eres/ssoapi";
import { exToMessage } from "@eres/ssoapi/api";
import { hideLoading, showInfo, showLoading } from "@my-dld/utils/UIUtils";
import { RegExRequirements } from "../../../../typings/global";
import { SSOProfile } from "@10x/sso/SSOProfile";
import UserNotification from "../../../components/UserNotification";
import { setSSOUser } from "@my-dld/utils/User";

interface Props extends RoutedViewProps {

}

interface State {
	current: string;
	newPwd: string;
	newPwdConfirm: string;
	formErrors: { [ k: string ]: string | undefined };
	isSamePwd: boolean;
	can: boolean;
}

export default class ChangePassword extends Component<Props, State> {
	constructor( props: Props ) {
		super( props );
		const p = SSOProfile.getProfile();
		this.state = {
			current: "",
			newPwd: "",
			newPwdConfirm: "",
			formErrors: {},
			isSamePwd: false,
			can: ( p && p.userAccountIDs && p.userAccountIDs.length > 0 ) || false,
		};
	}

	showDone = () => {
		showInfo( Strings.password_updated_msg, {
			iconWrap: "check-icon",
			icon: "fa fa-check",
			okLabel: Strings.close,
			title: LVStrings.confirmation
		} );
	};
	onEdit = ( e: MouseEvent ) => {
		e.preventDefault();
		navigateWithInParent( "profile", "edit" );
	};
	isValid = () => {
		let isError = this.state.isSamePwd || StringUtil.isEmptyStringOrNull( this.state.current );
		isError = isError || StringUtil.isEmptyStringOrNull( this.state.newPwd );
		isError = isError || StringUtil.isEmptyStringOrNull( this.state.newPwdConfirm );
		if ( !isError ) {
			const evs = Object.values( this.state.formErrors );
			isError = evs.filter( x => !StringUtil.isEmptyStringOrNull( x ) ).length !== 0;
		}
		return !isError;
	};

	render() {
		const { can } = this.state;
		const { location } = this.props;
		const notification = location?.state?.notification;
		if ( !can ) {
			return <PageContent title={ Strings.change_password }>
				<PageTitle title={ Strings.change_password } paths={ [ DashboardBCItem, MyProfileBCItem ] }/>
				<div className="row change-password-wrapper">
					<div className="change-password-title">
						<span className="change-pass-icon"/>
						<h5>{ Strings.change_password }</h5>
					</div>
					<div
						className="invalid-feedback invalid-feedback-x">{ Strings.please_login_uid_update_password }</div>
				</div>
			</PageContent>;
		}
		const isValid = this.isValid();
		return <PageContent title={ Strings.change_password } hideQLinks={ true }>
			<PageTitle title={ Strings.change_password } paths={ [ DashboardBCItem, MyProfileBCItem ] }/>
			{ notification != null &&
				<UserNotification
					notification={ notification } wrapperClass={ "col-md-12 d-flex" }
					alertClass={ "flex-grow-1" }/> }
			<div className="row change-password-wrapper">
				<div className={ "col-md-12" }>
					<TextInputField
						containerClassName={ "form-group mb-4 lock-icon" }
						inputClassName={ "form-control" }
						placeHolder={ Strings.current_password }
						id={ "current" }
						holder={ this }
						error={ this.state.formErrors.current }
						label={ Strings.current_password }
						required={ true }
						maxLength={ 250 }
						inputProps={ {
							minLength: 8,
							type: "password"
						} }
						onValueChange={ ( value, extra ) => {
							this.validateCurrentPwd( value );
						} }
					/>

					<TextInputField
						containerClassName={ "form-group mb-4 lock-icon" }
						inputClassName={ "form-control" }
						placeHolder={ Strings.new_password }
						id={ "newPwd" }
						holder={ this }
						error={ this.state.formErrors.newPwd }
						label={ Strings.new_password }
						required={ true }
						maxLength={ 250 }
						inputProps={ {
							type: "password"
						} }
						onValueChange={ ( value, extra ) => {
							this.validatePwd( value );
						} }
						onValueUpdated={ ( value: string ) => {
							if ( !StringUtil.isEmptyStringOrNull( this.state.newPwdConfirm ) )
								this.validateConfirmPwd( this.state.newPwdConfirm );
						} }
					>
						<PasswordMeter value={ this.state.newPwd }/>
						{ this.state.isSamePwd &&
							<div className="invalid-feedback invalid-feedback-x">{ Strings.new_current_pwd_same }</div> }
					</TextInputField>

					<TextInputField
						containerClassName={ "form-group mb-4 lock-icon" }
						inputClassName={ "form-control" }
						placeHolder={ Strings.confirm_new_password }
						id={ "newPwdConfirm" }
						holder={ this }
						error={ this.state.formErrors.newPwdConfirm }
						label={ Strings.confirm_new_password }
						required={ true }
						maxLength={ 250 }
						inputProps={ {
							type: "password"
						} }
						onValueChange={ ( value, extra ) => {
							this.validateConfirmPwd( value );
						} }
					/>

					<div className="form-group">
						<button
							disabled={ !isValid } type="button" onClick={ this.submit }
							className="btn btn_1">{ Strings.submit_label }</button>
						<a href="#/mydld/profile" className="ms-3 me-3">{ Strings.cancel }</a>
					</div>
				</div>
			</div>
		</PageContent>;
	}

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any ) {
		if ( this.state.current === this.state.newPwd && !this.state.isSamePwd ) {
			this.setState( { isSamePwd: true } );
		} else if ( this.state.current !== this.state.newPwd && this.state.isSamePwd ) {
			this.setState( { isSamePwd: false } );
		}
	}

	setError = ( key: string, error: string | undefined ) => {
		this.setState( s => {
			return {
				formErrors: {
					...s.formErrors,
					[ key ]: error,
				}
			};
		} );
	};
	validateCurrentPwd = ( value: string ) => {
		if ( StringUtil.isEmptyStringOrNull( value ) ) {
			this.setError( "current", Strings.form_required_label );
		} else {
			this.setError( "current", undefined );
		}
	};
	validatePwd = ( value: string ) => {
		if ( StringUtil.isEmptyStringOrNull( value ) ) {
			this.setError( "newPwd", Strings.form_required_label );
		} else {
			try {
				validateNewPassword( value );
				this.setError( "newPwd", undefined );
			} catch ( e ) {
				this.setError( "newPwd", Strings.password_req_fail );
			}
		}
	};
	validateConfirmPwd = ( value: string ) => {
		if ( value !== this.state.newPwd ) {
			this.setError( "newPwdConfirm", Strings.passwords_doesnt_match );
		} else {
			this.setError( "newPwdConfirm", undefined );
		}
	};
	submit = () => {
		showLoading();
		SSOApi.profile.changePassword( this.state.current, this.state.newPwd ).then( r => {
			return SSOApi.profile.me().then( r => {
				setSSOUser( r.data );
				hideLoading();
				this.showDone();
				replaceNavigate( "/mydld/profile" );
			} );
		} )
		.catch( e => {
			return exToMessage( e )
			.then( SSOApi.profile.handleProfileException )
			.then( correction => {
				const isCorrected = correction.action.task( correction.data );
				if ( isCorrected ) {
					hideLoading();
					this.showDone();
					replaceNavigate( "/mydld/profile" );
				}
				return Promise.reject( correction.error );
			} ).catch( e => {
				hideLoading();
				exToMessage( e ).then( message => {
					hideLoading();
					let key = "newPwd";
					if ( message.src && message.src === "old" ) {
						key = "current";
					}
					this.setError( key, message.error );
				} );
			} );

		} );
	};
}


export function PasswordMeter( props: { value: string } ) {
	const r = PasswordsRequirements();
	let normalMatches = r.filter( x => !x.isTopLevel );
	let topLevels = r.filter( x => x.isTopLevel );
	let isValid = false;
	try {
		isValid = validateNewPassword( props.value, true );
	} catch ( e ) {

	}
	return <div className={ "pwd-checker" }>
		<ul>
			{ normalMatches.map( ( value, index ) => {
				let isOky = true;
				try {
					validateRegExRequirement( value, props.value );
				} catch ( e ) {
					isOky = false;
				}
				return <li className={ isOky ? "valid-password" : "invalid-password" } key={ value.id }>
					<small>{ Strings[ value.message ] }</small>
				</li>;
			} ) }
			{ isValid && topLevels.map( ( value, index ) => {
				let isOky = true;
				try {
					validateRegExRequirement( value, props.value );
				} catch ( e ) {
					isOky = false;
				}
				if ( !isOky )
					return <li className={ isOky ? "valid-password" : "invalid-password" } key={ value.id }>
						<small>{ Strings[ value.message ] }</small>
					</li>;
				return null;
			} ) }
		</ul>
	</div>;
}

export function validateRegExRequirement( req: RegExRequirements, password: string ) {
	if ( req.match != null ) {
		let rx: RegExp | undefined = undefined;
		if ( typeof req.match === "string" ) {
			let match = req.match.match( new RegExp( "^/(.*?)/([gimy]*)$" ) );
			if ( match ) {
				rx = new RegExp( match[ 1 ], match[ 2 ] );
				req.match = rx;
			}
		} else {
			rx = req.match;
		}
		if ( rx && rx.test != null ) {
			const isOky = rx.test( password );
			if ( !isOky ) {
				throw LocaleUtil.getLocalValue( req.message );
			}
		}
	}
	return true;
}

export function validateNewPassword( password: string, skipTop: boolean = false ) {
	let error: string[] = [];
	let reqs = PasswordsRequirements( skipTop );
	for ( const passwordReq of reqs ) {
		try {
			validateRegExRequirement( passwordReq, password );
		} catch ( e ) {
			error.push( e );
		}
	}
	if ( error.length === 0 ) return true;
	throw error;
}

export function PasswordsRequirements( skipTop: boolean = false ): RegExRequirements[] {
	const x: RegExRequirements[] = [];
	if ( window.AppInfo.ssoPassword && Array.isArray( window.AppInfo.ssoPassword ) && window.AppInfo.ssoPassword.length > 0 ) {
		let reqs = skipTop ? window.AppInfo.ssoPassword.filter( x => !x.isTopLevel ) : window.AppInfo.ssoPassword;
		x.push( ...reqs );
	}
	return x;
}

export function UserNameRequirements( skipTop: boolean = false ): RegExRequirements[] {
	const x: RegExRequirements[] = [];
	if ( window.AppInfo.ssoUsername && Array.isArray( window.AppInfo.ssoUsername ) && window.AppInfo.ssoUsername.length > 0 ) {
		let reqs = skipTop ? window.AppInfo.ssoUsername.filter( x => !x.isTopLevel ) : window.AppInfo.ssoUsername;
		x.push( ...reqs );
	}
	return x;
}
